import { useEffect } from 'react';
import type { SeoMetadata } from '@/types/seo';

type SeoProps = SeoMetadata & {
  type?: 'website' | 'article';
  jsonLd?: Record<string, unknown>;
};

export function Seo({ title, description, canonical, keywords = [], image, type = 'website', jsonLd }: SeoProps) {
  useEffect(() => {
    document.title = title;
    setMeta('description', description);
    setMeta('keywords', keywords.join(', '));
    setLink('canonical', canonical);
    setMetaProperty('og:title', title);
    setMetaProperty('og:description', description);
    setMetaProperty('og:type', type);
    setMetaProperty('og:url', canonical);
    if (image) setMetaProperty('og:image', image);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    if (image) setMeta('twitter:image', image);
    setJsonLd(jsonLd);
  }, [canonical, description, image, jsonLd, keywords, title, type]);

  return null;
}

function setMeta(name: string, content: string) {
  const selector = `meta[name="${name}"]`;
  const meta = document.querySelector<HTMLMetaElement>(selector) ?? document.createElement('meta');
  meta.name = name;
  meta.content = content;
  if (!meta.parentNode) document.head.append(meta);
}

function setMetaProperty(property: string, content: string) {
  const selector = `meta[property="${property}"]`;
  const meta = document.querySelector<HTMLMetaElement>(selector) ?? document.createElement('meta');
  meta.setAttribute('property', property);
  meta.content = content;
  if (!meta.parentNode) document.head.append(meta);
}

function setLink(rel: string, href: string) {
  const selector = `link[rel="${rel}"]`;
  const link = document.querySelector<HTMLLinkElement>(selector) ?? document.createElement('link');
  link.rel = rel;
  link.href = href;
  if (!link.parentNode) document.head.append(link);
}

function setJsonLd(jsonLd?: Record<string, unknown>) {
  const id = 'divergente-json-ld';
  const existing = document.getElementById(id);

  if (!jsonLd) {
    existing?.remove();
    return;
  }

  const script = (existing ?? document.createElement('script')) as HTMLScriptElement;
  script.id = id;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(jsonLd);
  if (!script.parentNode) document.head.append(script);
}
